import RefreshIcon from "@mui/icons-material/CachedRounded";
import DeleteIcon from "@mui/icons-material/DeleteOutlineRounded";
import FileUploadIcon from "@mui/icons-material/FileUploadOutlined";
import FolderIcon from "@mui/icons-material/FolderRounded";
import DownloadIcon from "@mui/icons-material/SaveAltRounded";
import { Button } from "@mui/material";
import { yellow } from "@mui/material/colors";
import { useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import ActionsMenuButton from "@entrilia/shared/components/ActionsMenuButton";
import FileIcon from "@entrilia/shared/components/FileIcon";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import { logError } from "@entrilia/shared/logging";
import { downloadFileFromUrl } from "@entrilia/shared/services/downloadFile";
import adminApi from "../../../../../api/adminApi";
import type { DataImportFileContainerInfo, DataImportInfo } from "../../../../../api/types/dataImportTypes";
import FileContainerDialog from "../file-container/FileContainerDialog";

interface Props {
  dataImport: DataImportInfo;
  showDownload?: boolean;
  showValidate?: boolean;
  showDelete?: boolean;
  disabled?: boolean;
  onValidate: () => void;
  onDelete: () => void;
}

const getCsvFileDownloadInfo = withErrorHandling(adminApi.getDataImportCsvFileDownloadInfo);

const getValidationSummaryCsvFileDownloadInfo = withErrorHandling(
  adminApi.getDataImportValidationSummaryCsvFileDownloadInfo
);

const ReviewImportActions = ({
  dataImport,
  showDownload,
  showValidate,
  showDelete,
  disabled,
  onValidate,
  onDelete,
}: Props) => {
  const { sendNotificationError } = useNotificationContext();

  const [downloading, setDownloading] = useState(false);

  const [editedFileContainerInfo, setEditedFileContainerInfo] = useState<DataImportFileContainerInfo>();

  const handleDownloadImportedFiles = async () => {
    setDownloading(true);

    const fileInfoTasks = dataImport.files.map((file) =>
      getCsvFileDownloadInfo(dataImport.id, {
        fileDataCatalogueId: file.sourceFileDataCatalogueId,
        includedActions: "All",
        includeDataImportMetadata: true,
      })
    );

    const results = await Promise.all(fileInfoTasks);

    setDownloading(false);

    const failedResults = results.filter(([, error]) => !!error);

    if (failedResults.length > 0) {
      logError(failedResults[0]?.[1], "[ReviewImportActions] getCsvFileDownloadInfo");
      sendNotificationError(
        `Could not obtain file download information from ${failedResults.length} files of ${dataImport.files.length}.`
      );
      return;
    }

    const downloadUrls = results.map(([resp]) => resp?.downloadUrl ?? "").filter(Boolean);
    await Promise.all(downloadUrls.map((url) => downloadFileFromUrl(url)));
  };

  const handleDownloadValidationSummary = async () => {
    setDownloading(true);
    const [resp, error] = await getValidationSummaryCsvFileDownloadInfo(dataImport.id);
    setDownloading(false);

    if (error) {
      logError(error, "[ReviewImportActions] getValidationSummaryCsvFileDownloadInfo");
      sendNotificationError(error.message);
      return;
    }

    await downloadFileFromUrl(resp.downloadUrl);
  };

  const showUpload = dataImport.fileContainerInfos.length > 0;

  return (
    <>
      {showUpload && (
        <ActionsMenuButton
          text="Upload"
          icon={<FileUploadIcon />}
          variant="outlined"
          color="secondary"
          disabled={disabled}
          items={dataImport.fileContainerInfos.map((fileContainer) => ({
            label: fileContainer.containerName,
            icon: <FolderIcon sx={{ color: yellow[600] }} />,
            onClick: () => setEditedFileContainerInfo(fileContainer),
          }))}
        />
      )}

      {showDownload && (
        <ActionsMenuButton
          text="Download"
          icon={<DownloadIcon />}
          variant="outlined"
          color="secondary"
          loading={downloading}
          disabled={disabled}
          items={[
            {
              label: dataImport.files.length > 1 ? `Imported Files (${dataImport.files.length})` : "Imported File",
              icon: <FileIcon fileExtension=".csv" />,
              onClick: handleDownloadImportedFiles,
              disabled: dataImport.files.length === 0,
            },
            {
              label: "Validation Summary CSV",
              icon: <FileIcon fileExtension=".csv" />,
              onClick: handleDownloadValidationSummary,
            },
          ]}
        />
      )}

      {showValidate && (
        <Button variant="outlined" onClick={onValidate} startIcon={<RefreshIcon />} disabled={disabled}>
          Validate
        </Button>
      )}

      {showDelete && (
        <Button variant="outlined" color="error" onClick={onDelete} startIcon={<DeleteIcon />} disabled={disabled}>
          Delete
        </Button>
      )}

      {editedFileContainerInfo && (
        <FileContainerDialog
          mode="update"
          editedFileContainerInfo={editedFileContainerInfo}
          onClose={() => setEditedFileContainerInfo(undefined)}
          onSaved={() => setEditedFileContainerInfo(undefined)}
        />
      )}
    </>
  );
};

export default ReviewImportActions;
